import MuleBadge from '../../components/MuleBadge';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronRightIcon, ChevronUpIcon, ChevronDownIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import userSettingsService from '../../services/userSettings.service';
import TagBadge, { TagColor } from '../../components/TagBadge';
import {
  getQualificationColor,
  parseQualifications,
} from '../../constants/driverQualifications';
import ParkingLocationModal, { getParkingColorStyles } from '../../components/ParkingLocationModal';
import { Tooltip } from '../../components/ui/Tooltip';
import MultiSelect from '../../components/MultiSelect';
import DriverPhoneLink from '../../components/DriverPhoneLink';
import { useWorkingStatusPreference } from '../../contexts/WorkingStatusPreferenceContext';
import { matchesDriverPreference } from '../../utils/driverPreferenceFilter';
import { groupByPreferenceBucket, PreferenceBucket } from '../../utils/driverPreferenceGroups';
import {
  WORKING_CATEGORY_LIVE_DISPATCH,
  WORKING_CATEGORY_GROUNDED_YARD,
  WORKING_CATEGORY_DONE_FOR_DAY,
  getWorkingCategoryLabel,
} from '../../constants/workingCategories';

interface DeliveryCount {
  location_name: string;
  count: number;
  is_return: boolean;
  is_live: boolean;
  accepted_count: number;
  appointment_time?: string;  // Optional appointment time for individual deliveries
  leg_action?: string;  // e.g. DELIVERY, PICKUP, STOPOFF, RETURN
}

interface DayDeliveryData {
  delivery_counts: DeliveryCount[];
  total_deliveries: number;
  accepted_deliveries: number;
  status?: string | null;
  oos_comments?: string;
  oos_type?: string;
  comments?: string;
  permits_requested?: boolean;
  port_appointment_made?: boolean;
  confirmed?: boolean;
  workingcategory?: string | null;
}

interface DriverPlanningWeekData {
  driver: string;
  driver_name: string;
  isMule?: boolean;
  preference: string;  // OTR or LOCAL
  parkingloc: number | null;
  parking_location_name: string;
  parking_color: number | null;
  start_time: string | null;  // HH:MM:SS from driver_settings, same source as the day view
  start_time_qualifier: 'before' | 'at' | 'after' | null;
  region: string;
  dispatcher_region?: string;
  endorsements?: string | null;  // Format: "Hazmat, Tank, 93K, Daycab, Sleeper, < 15K"
  delivery_data: Record<string, DayDeliveryData>;
  textmsgnumber?: string;
  homephone?: string;
}

interface DispatcherGroup {
  dispatcher: string;
  region: string;
  drivers: DriverPlanningWeekData[];
}

interface WeekOption {
  value: string;
  label: string;
}

interface DriverPlanningWeekProps {
  selectedDispatchers: string[];
  selectedRegions: string[];
  selectedDriverType: string[];
  /** Free-text filter: only drivers with a move at a matching location this week. */
  deliveryLocationQuery?: string;
  weekStart: Date;
  refreshTrigger: number;
  groupByDispatcher: boolean;
  groupByPreference: boolean;
  showStatus: boolean;
  showAppointmentTimes: boolean;
  showQualifications: boolean;
  onOpenLegsDrawer: (driver: string, driverName: string, dateKey: string) => void;
  weekOptions: WeekOption[];
  canGoBack: boolean;
  canGoForward: boolean;
  onWeekChange: (dateKey: string) => void;
  onNavigateWeek: (direction: number) => void;
  usernameToDisplayname: Map<string, string>;
}

// Format date as YYYY-MM-DD
const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format date for local display without timezone issues
const formatLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Convert name to title case
const toTitleCase = (str: string | undefined) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Sort drivers within a group by saved order, with unsaved drivers at the end
const sortDriversByOrder = (
  drivers: DriverPlanningWeekData[],
  savedOrder: string[] | undefined
): DriverPlanningWeekData[] => {
  if (!savedOrder || savedOrder.length === 0) {
    return drivers;
  }

  const orderMap = new Map(savedOrder.map((id, index) => [id, index]));

  return [...drivers].sort((a, b) => {
    const aIndex = orderMap.get(a.driver);
    const bIndex = orderMap.get(b.driver);

    // Both have saved positions - sort by saved order
    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }
    // Only a has saved position - a comes first
    if (aIndex !== undefined) return -1;
    // Only b has saved position - b comes first
    if (bIndex !== undefined) return 1;
    // Neither has saved position - maintain original order
    return 0;
  });
};

// Get status color classes
const getStatusColorClass = (status: string | null | undefined) => {
  switch (status) {
    case 'Out Of Service':
    case 'Out of Service':
      return 'bg-danger text-danger-fg';
    case 'Not Working':
    case 'NOT WORKING':
      return 'bg-info text-info-fg';
    default:
      return '';
  }
};

// Port terminal regions that require permits/appointments
const PORT_TERMINALS = ['Mobile', 'Savannah', 'Charleston', 'Baltimore'];

const isPortTerminal = (region: string | undefined) => {
  if (!region) return false;
  return PORT_TERMINALS.some(terminal => region.toLowerCase().includes(terminal.toLowerCase()));
};


// Get working status badge for a day
interface BadgeParams {
  status: string | null | undefined;
  hasDeliveries: boolean;
  region?: string;
  permitsRequested?: boolean;
  portAppointmentMade?: boolean;
  confirmed?: boolean;
  workingcategory?: string | null;
}

interface BadgeResult {
  text: string;
  colorClass: string;
  tooltip: string;
  /** Render as a labeled pill at the top of the cell instead of the corner letter. */
  wide?: boolean;
}

/**
 * Driver start-time chip label: 12-hour time with the before/after qualifier
 * folded in ("Before 7:00 AM"). Null when the driver has no start time set.
 * Accepts the TIME column's HH:MM:SS as well as HH:MM.
 */
export const formatStartTimeChip = (
  time: string | null | undefined,
  qualifier: 'before' | 'at' | 'after' | null | undefined,
): string | null => {
  if (!time) return null;
  const [hoursStr, minutesStr = '00'] = time.split(':');
  const hours = parseInt(hoursStr, 10);
  if (Number.isNaN(hours)) return null;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const label = `${displayHours}:${minutesStr} ${period}`;
  if (qualifier === 'before') return `Before ${label}`;
  if (qualifier === 'after') return `After ${label}`;
  return label;
};

export const getWorkingStatusBadge = (params: BadgeParams): BadgeResult | null => {
  const { status, hasDeliveries, region, permitsRequested, portAppointmentMade, confirmed, workingcategory } = params;

  // Out Of Service - Don't show badge, cell already displays status
  if (status?.toLowerCase().includes('out')) {
    return null;
  }
  // Not Working - Don't show badge, cell already displays status
  if (status === 'Not Working' || status === 'NOT WORKING') {
    return null;
  }
  // Available, Planned, Planned Accepted, or has deliveries without OOS/NW
  if (hasDeliveries || status === 'Available' || status?.includes('Planned')) {
    // A working category gets its own color, outranking the yellow warnings
    // (category drivers are almost never confirmed, so yellow-first made
    // these unreachable). "Do Not Preplan" is stored as 'Live Dispatch' — see
    // workingCategories.ts. Do Not Preplan / Grounded Yard keep their color
    // on Planned / Accepted days too (2026-09-08), like the other two views.
    // So does Done For The Day — a finished driver nearly always has legs,
    // so his day reads Planned / Accepted, never Available. Its badge is a
    // wide pill that prints the full label — a bare "W" hid the one fact the
    // category exists to show.
    const isPlannedDay = status?.includes('Planned') === true;
    if ((status === 'Available' || isPlannedDay) && workingcategory === WORKING_CATEGORY_LIVE_DISPATCH) {
      return { text: 'W', colorClass: 'bg-orange-500 text-white', tooltip: 'Working - Do Not Preplan' };
    }
    if ((status === 'Available' || isPlannedDay) && workingcategory === WORKING_CATEGORY_GROUNDED_YARD) {
      return { text: 'W', colorClass: 'bg-purple-500 text-white', tooltip: 'Working - Grounded Yard' };
    }
    if ((status === 'Available' || isPlannedDay) && workingcategory === WORKING_CATEGORY_DONE_FOR_DAY) {
      return {
        text: getWorkingCategoryLabel(WORKING_CATEGORY_DONE_FOR_DAY),
        colorClass: 'bg-sky-500 text-white',
        tooltip: 'Working - Done For The Day',
        wide: true,
      };
    }
    // Check if needs yellow warning badge
    // Not confirmed
    if (!confirmed) {
      return { text: 'W', colorClass: 'bg-yellow-400 text-yellow-900', tooltip: 'Working - Not Confirmed' };
    }
    // Port terminal and missing permits or appointment
    if (isPortTerminal(region) && (!permitsRequested || !portAppointmentMade)) {
      const missing = [];
      if (!permitsRequested) missing.push('Permits');
      if (!portAppointmentMade) missing.push('Appointment');
      return { text: 'W', colorClass: 'bg-yellow-400 text-yellow-900', tooltip: `Working - Pending ${missing.join(' & ')}` };
    }
    // All good - Green W
    return { text: 'W', colorClass: 'bg-green-500 text-white', tooltip: 'Working' };
  }
  // Unknown - Grey U
  return { text: 'U', colorClass: 'bg-gray-400 text-white', tooltip: 'Unknown' };
};

// Cell fill used when the status toggle is off: converts a badge color to a
// background fill + text color, matching the Working/Not Working tab palette.
export const getStatusFillClass = (badgeColorClass: string, actualStatus: string | null | undefined): string => {
  if (badgeColorClass.includes('orange')) return 'bg-orange-100 text-orange-700';
  if (badgeColorClass.includes('purple')) return 'bg-purple-100 text-purple-700';
  // Done For The Day — sky, not the Not Working bg-info blue.
  if (badgeColorClass.includes('sky')) return 'bg-sky-300 text-sky-900';
  if (badgeColorClass.includes('green')) {
    return actualStatus === 'Available' ? 'bg-green-100 text-green-700' : 'bg-green-600 text-white';
  }
  if (badgeColorClass.includes('yellow')) {
    return actualStatus === 'Available' ? 'bg-flag text-flag-fg' : 'bg-yellow-400 text-yellow-900';
  }
  if (badgeColorClass.includes('gray')) return 'text-gray-400';
  return '';
};

// Text printed inside the filled cell (status toggle off, no deliveries). An
// Available day with a working category prints the category label — the
// other two views already do — so a sky-blue cell says "Done For The Day",
// not "Available".
export const getStatusFillText = (
  status: string | null | undefined,
  workingcategory: string | null | undefined
): string => {
  if (!status) return '';
  if (status === 'Available' && workingcategory) return getWorkingCategoryLabel(workingcategory);
  return status;
};

// Status filter options
const statusFilterOptions = [
  { value: 'Has Deliveries', label: 'Has Deliveries' },
  { value: 'Out Of Service', label: 'Out of Service' },
  { value: 'Not Working', label: 'Not Working' },
  { value: 'No Status', label: 'No Status' },
];

// localStorage key for persisting collapsed days
/**
 * True when the driver has at least one move (any leg action) on one of the
 * shown days whose location name contains the query. A blank query matches
 * every driver so the board is unfiltered until the user types.
 */
export const driverHasDeliveryLocationMatch = (
  deliveryData: Record<string, Pick<DayDeliveryData, 'delivery_counts'>> | undefined,
  query: string,
  dateKeys: string[],
): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  if (!deliveryData) return false;
  return dateKeys.some(dateKey =>
    (deliveryData[dateKey]?.delivery_counts || []).some(delivery =>
      (delivery.location_name || '').toLowerCase().includes(needle)
    )
  );
};

const COLLAPSED_DAYS_STORAGE_KEY = 'driverPlanningWeek_collapsedDays';

const DriverPlanningWeek: React.FC<DriverPlanningWeekProps> = ({
  selectedDispatchers,
  selectedRegions,
  selectedDriverType,
  deliveryLocationQuery = '',
  weekStart,
  refreshTrigger,
  groupByDispatcher,
  groupByPreference,
  showStatus,
  showAppointmentTimes,
  showQualifications,
  onOpenLegsDrawer,
  weekOptions,
  canGoBack,
  canGoForward,
  onWeekChange,
  onNavigateWeek,
  usernameToDisplayname,
}) => {
  const { planningShowAllMoves } = useWorkingStatusPreference();
  const [data, setData] = useState<DispatcherGroup[]>([]);
  const [loading, setLoading] = useState(true);
  // Distinguish "the load failed" from "genuinely no data" (2026-08-25 sweep).
  const [loadFailed, setLoadFailed] = useState(false);
  // Ref, NOT state: as state (and a dep of the fetch effect) the flag's flip
  // after the first load re-ran the effect, so every page load fetched twice.
  const initialLoadCompleteRef = useRef(false);
  const [expandedDispatchers, setExpandedDispatchers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<number, string[]>>({});
  const [collapsedDays, setCollapsedDays] = useState<Set<number>>(() => {
    // Initialize from localStorage or default to empty
    try {
      const stored = localStorage.getItem(COLLAPSED_DAYS_STORAGE_KEY);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch {
      // Ignore localStorage errors
    }
    return new Set();
  });

  // Collapsed LOCAL/OTR sub-groups, keyed `${dispatcher}|${bucket}` so collapsing
  // one dispatcher's LOCAL block leaves the other dispatchers' alone
  const [collapsedPrefGroups, setCollapsedPrefGroups] = useState<Set<string>>(new Set());

  // Parking location modal state
  const [parkingModalOpen, setParkingModalOpen] = useState(false);
  const [parkingModalDriver, setParkingModalDriver] = useState('');
  const [parkingModalDriverName, setParkingModalDriverName] = useState('');
  const [parkingModalCurrentLoc, setParkingModalCurrentLoc] = useState<number | null>(null);
  const [parkingModalCurrentName, setParkingModalCurrentName] = useState('');
  const [parkingModalCurrentColor, setParkingModalCurrentColor] = useState<number | null>(null);

  // Sorting state: 'driver' for unit number, or day index (0-6) for day columns
  const [sortColumn, setSortColumn] = useState<'driver' | number | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Driver sort order (from Drivers page drag-and-drop)
  const [driverSortOrder, setDriverSortOrder] = useState<Record<string, string[]>>({});

  // Load driver sort order from user settings
  useEffect(() => {
    const loadSortOrder = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('drivers_sort_order');
        if (setting?.setting_value) {
          setDriverSortOrder(setting.setting_value);
        }
      } catch (error) {
        console.error('Failed to load driver sort order:', error);
      }
    };
    loadSortOrder();
  }, []);

  const handleSort = (column: 'driver' | number) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending for driver, descending for days (most moves first)
      setSortColumn(column);
      setSortDirection(column === 'driver' ? 'asc' : 'desc');
    }
  };

  // Generate week days from weekStart
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }, [weekStart]);
  const weekDateKeys = useMemo(() => weekDays.map(formatDateKey), [weekDays]);

  const formatDayHeader = (date: Date) => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayInitials = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    return {
      day: dayNames[date.getDay()],
      initial: dayInitials[date.getDay()],
      date: date.getDate(),
    };
  };

  // Initialize collapsed days based on today's date when week changes
  useEffect(() => {
    const today = formatLocalDateKey(new Date());
    const newCollapsed = new Set<number>();

    weekDays.forEach((day, index) => {
      const dateKey = formatDateKey(day);
      // Collapse past days (before today)
      if (dateKey < today) {
        newCollapsed.add(index);
      }
    });

    setCollapsedDays(newCollapsed);
  }, [weekStart]); // Only re-run when week changes

  // Persist collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_DAYS_STORAGE_KEY, JSON.stringify([...collapsedDays]));
    } catch {
      // Ignore localStorage errors
    }
  }, [collapsedDays]);

  // Toggle a day's collapsed state
  const toggleDayCollapsed = (dayIndex: number) => {
    setCollapsedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dayIndex)) {
        newSet.delete(dayIndex);
      } else {
        newSet.add(dayIndex);
      }
      return newSet;
    });
  };

  // Fetch data when week changes or refresh triggered
  useEffect(() => {
    const fetchData = async () => {
      // Only show loading on initial load, not on background refresh
      const isBackgroundRefresh = initialLoadCompleteRef.current;

      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      try {
        const startDate = formatDateKey(weekStart);
        const endDate = formatDateKey(weekDays[6]);

        const response = await api.get('/safety/driver-planning/weekly', {
          params: {
            start_date: startDate,
            end_date: endDate,
            show_appointment_times: showAppointmentTimes,
            show_all_moves: planningShowAllMoves
          }
        });

        setData(response.data || []);
        setLoadFailed(false);
        initialLoadCompleteRef.current = true;
      } catch (error) {
        console.error('Error fetching weekly driver planning data:', error);
        // Only clear data on initial load failure, not background refresh
        if (!isBackgroundRefresh) {
          setData([]);
          setLoadFailed(true);
        }
      } finally {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [weekStart, weekDays, refreshTrigger, showAppointmentTimes, planningShowAllMoves]);

  // Expand all dispatchers by default when data loads
  useEffect(() => {
    if (data.length > 0) {
      setExpandedDispatchers(new Set(data.map(g => g.dispatcher)));
    }
  }, [data]);

  // Helper to get status category for filtering
  const getStatusCategory = (dayData: DayDeliveryData | undefined): string => {
    if (!dayData) return 'No Status';
    if (dayData.delivery_counts.length > 0) return 'Has Deliveries';
    if (dayData.status === 'Out Of Service' || dayData.status === 'Out of Service') return 'Out Of Service';
    if (dayData.status === 'Not Working' || dayData.status === 'NOT WORKING') return 'Not Working';
    return 'No Status';
  };

  // Filter data based on selections
  const filteredData = useMemo(() => {
    // Sort function for drivers (defined inside useMemo to capture current values)
    const sortDrivers = (drivers: DriverPlanningWeekData[], dispatcher: string) => {
      if (sortColumn === null) {
        // Apply saved driver sort order as default
        return sortDriversByOrder(drivers, driverSortOrder[dispatcher]);
      }

      return [...drivers].sort((a, b) => {
        let comparison = 0;

        if (sortColumn === 'driver') {
          // Sort by unit number numerically (parse as integers to handle leading zeros)
          const aNum = parseInt(a.driver, 10) || 0;
          const bNum = parseInt(b.driver, 10) || 0;
          comparison = aNum - bNum;
        } else {
          // Sort by number of moves for a specific day
          const dateKey = formatDateKey(weekDays[sortColumn]);
          const aData = a.delivery_data[dateKey];
          const bData = b.delivery_data[dateKey];
          const aMoves = aData?.total_deliveries || 0;
          const bMoves = bData?.total_deliveries || 0;
          comparison = aMoves - bMoves;
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    };

    const selectedDisplaynames = selectedDispatchers.length > 0
      ? new Set(selectedDispatchers.map(username => usernameToDisplayname.get(username) || username))
      : null;

    const filtered = data
      .filter(group => {
        if (selectedDisplaynames && !selectedDisplaynames.has(group.dispatcher)) {
          return false;
        }
        return true;
      })
      .map(group => ({
        ...group,
        drivers: group.drivers.filter(driver => {
          if (selectedRegions.length > 0 && !selectedRegions.includes(driver.region)) {
            return false;
          }
          // Filter by driver preference (LOCAL / OTR)
          if (!matchesDriverPreference(driver.preference, selectedDriverType)) {
            return false;
          }
          // Filter by search query (driver number, name, or parking location)
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const driverMatch = driver.driver.toLowerCase().includes(query);
            const nameMatch = driver.driver_name?.toLowerCase().includes(query);
            const parkingLocMatch = driver.parking_location_name?.toLowerCase().includes(query);
            if (!driverMatch && !nameMatch && !parkingLocMatch) {
              return false;
            }
          }
          // Filter by delivery location (any move this week at a matching location)
          if (!driverHasDeliveryLocationMatch(driver.delivery_data, deliveryLocationQuery, weekDateKeys)) {
            return false;
          }
          // Filter by column status filters
          for (let dayIndex = 0; dayIndex < weekDays.length; dayIndex++) {
            const filters = columnFilters[dayIndex];
            if (filters && filters.length > 0) {
              const dateKey = formatDateKey(weekDays[dayIndex]);
              const dayData = driver.delivery_data[dateKey];
              const statusCategory = getStatusCategory(dayData);
              if (!filters.includes(statusCategory)) {
                return false;
              }
            }
          }
          return true;
        })
      }))
      .filter(group => group.drivers.length > 0);

    // Apply sorting within each group (for grouped view)
    return filtered.map(group => ({
      ...group,
      drivers: sortDrivers(group.drivers, group.dispatcher)
    }));
  }, [data, selectedDispatchers, selectedRegions, selectedDriverType, searchQuery, deliveryLocationQuery, columnFilters, weekDays, weekDateKeys, sortColumn, sortDirection, driverSortOrder, usernameToDisplayname]);

  // For flat view, create a globally sorted list of all drivers with their dispatcher info
  const sortedFlatDrivers = useMemo(() => {
    if (groupByDispatcher || sortColumn === null) return null;

    // Collect all drivers with their dispatcher info
    const allDrivers: Array<{ driver: DriverPlanningWeekData; dispatcher: string }> = [];
    filteredData.forEach(group => {
      group.drivers.forEach(driver => {
        allDrivers.push({ driver, dispatcher: group.dispatcher });
      });
    });

    // Sort globally
    return allDrivers.sort((a, b) => {
      let comparison = 0;

      if (sortColumn === 'driver') {
        const aNum = parseInt(a.driver.driver, 10) || 0;
        const bNum = parseInt(b.driver.driver, 10) || 0;
        comparison = aNum - bNum;
      } else {
        const dateKey = formatDateKey(weekDays[sortColumn]);
        const aData = a.driver.delivery_data[dateKey];
        const bData = b.driver.delivery_data[dateKey];
        const aMoves = aData?.total_deliveries || 0;
        const bMoves = bData?.total_deliveries || 0;
        comparison = aMoves - bMoves;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, groupByDispatcher, sortColumn, sortDirection, weekDays]);

  // Calculate daily totals for footer
  const dailyStats = useMemo(() => {
    return weekDays.map(day => {
      const dateKey = formatDateKey(day);
      let totalDeliveries = 0;
      let acceptedDeliveries = 0;

      filteredData.forEach(group => {
        group.drivers.forEach(driver => {
          const dayData = driver.delivery_data[dateKey];
          if (dayData) {
            totalDeliveries += dayData.total_deliveries;
            acceptedDeliveries += dayData.accepted_deliveries;
          }
        });
      });

      const percentage = totalDeliveries > 0 ? (acceptedDeliveries / totalDeliveries) * 100 : 0;

      return {
        totalDeliveries,
        acceptedDeliveries,
        percentage
      };
    });
  }, [filteredData, weekDays]);

  // Get status counts for a specific day (for filter dropdown labels)
  const getStatusCountsForDay = (dayIndex: number): Record<string, number> => {
    const dateKey = formatDateKey(weekDays[dayIndex]);
    const counts: Record<string, number> = {
      'Has Deliveries': 0,
      'Out Of Service': 0,
      'Not Working': 0,
      'No Status': 0,
    };

    // Count from filtered data (after dispatcher/region/preference/search filters, but not column filters)
    data
      .filter(group => {
        if (selectedDispatchers.length === 0) return true;
        const displaynames = new Set(selectedDispatchers.map(username => usernameToDisplayname.get(username) || username));
        return displaynames.has(group.dispatcher);
      })
      .forEach(group => {
        group.drivers
          .filter(driver => {
            if (selectedRegions.length > 0 && !selectedRegions.includes(driver.region)) return false;
            if (!matchesDriverPreference(driver.preference, selectedDriverType)) return false;
            if (searchQuery) {
              const query = searchQuery.toLowerCase();
              const driverMatch = driver.driver.toLowerCase().includes(query);
              const nameMatch = driver.driver_name?.toLowerCase().includes(query);
              const parkingLocMatch = driver.parking_location_name?.toLowerCase().includes(query);
              if (!driverMatch && !nameMatch && !parkingLocMatch) return false;
            }
            if (!driverHasDeliveryLocationMatch(driver.delivery_data, deliveryLocationQuery, weekDateKeys)) return false;
            return true;
          })
          .forEach(driver => {
            const dayData = driver.delivery_data[dateKey];
            const category = getStatusCategory(dayData);
            counts[category] = (counts[category] || 0) + 1;
          });
      });

    return counts;
  };

  const toggleDispatcher = (dispatcher: string) => {
    setExpandedDispatchers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dispatcher)) {
        newSet.delete(dispatcher);
      } else {
        newSet.add(dispatcher);
      }
      return newSet;
    });
  };

  const prefGroupKey = (dispatcher: string, bucket: PreferenceBucket) => `${dispatcher}|${bucket}`;

  const togglePrefGroup = (dispatcher: string, bucket: PreferenceBucket) => {
    const key = prefGroupKey(dispatcher, bucket);
    setCollapsedPrefGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const openParkingModal = (driver: DriverPlanningWeekData) => {
    setParkingModalDriver(driver.driver);
    setParkingModalDriverName(driver.driver_name);
    setParkingModalCurrentLoc(driver.parkingloc);
    setParkingModalCurrentName(driver.parking_location_name);
    setParkingModalCurrentColor(driver.parking_color);
    setParkingModalOpen(true);
  };

  const handleParkingLocationSave = async () => {
    try {
      const startDate = formatDateKey(weekStart);
      const endDate = formatDateKey(weekDays[6]);
      const response = await api.get('/safety/driver-planning/weekly', {
        params: { start_date: startDate, end_date: endDate, show_all_moves: planningShowAllMoves }
      });
      setData(response.data || []);
    } catch (error) {
      console.error('Error refreshing driver planning weekly:', error);
    }
  };

  const handleCellClick = (driver: string, driverName: string, dateKey: string) => {
    onOpenLegsDrawer(driver, driverName, dateKey);
  };

  // Get badge color and tooltip based on leg action type
  const getLegBadgeInfo = (delivery: DeliveryCount): { color: TagColor; tooltip: string; suffix: string } => {
    const action = (delivery.leg_action || 'DELIVERY').toUpperCase();
    if (delivery.is_return) return { color: 'yellow', tooltip: 'Return', suffix: '' };
    switch (action) {
      case 'DELIVERY':
        return delivery.is_live
          ? { color: 'purple', tooltip: 'Live Delivery', suffix: ' (L)' }
          : { color: 'blue', tooltip: 'Drop Delivery', suffix: '' };
      case 'PICKUP':
        return { color: 'green', tooltip: 'Pickup', suffix: ' (P)' };
      case 'STOPOFF':
        return { color: 'orange', tooltip: 'Stopoff', suffix: ' (S)' };
      case 'GROUNDED':
        return { color: 'red', tooltip: 'Grounded', suffix: ' (G)' };
      default:
        return { color: 'gray', tooltip: action.charAt(0) + action.slice(1).toLowerCase(), suffix: '' };
    }
  };

  // Render delivery badges for a cell
  const renderDeliveryBadges = (deliveryCounts: DeliveryCount[]) => {
    if (!deliveryCounts || deliveryCounts.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-0.5">
        {deliveryCounts.map((delivery, idx) => {
          const { color, tooltip: tooltipText, suffix: labelSuffix } = getLegBadgeInfo(delivery);

          // Build label based on showAppointmentTimes toggle
          let label: string;
          if (showAppointmentTimes && delivery.appointment_time) {
            label = `${delivery.location_name} ${delivery.appointment_time}${labelSuffix}`;
          } else if (showAppointmentTimes && !delivery.appointment_time) {
            label = `${delivery.location_name}${labelSuffix}`;
          } else {
            label = `${delivery.location_name} x${delivery.count}${labelSuffix}`;
          }

          return (
            <Tooltip key={idx} content={tooltipText}>
              <TagBadge
                label={label}
                color={color}
                className="whitespace-nowrap text-[0.625rem]"
              />
            </Tooltip>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Calculate width for expanded columns to distribute space evenly
  // First column is 384px (w-96), Quals column is 100px (when shown), collapsed columns are 48px each
  const expandedCount = 7 - collapsedDays.size;
  const collapsedWidth = collapsedDays.size * 48;
  const qualsWidth = showQualifications ? 100 : 0;
  const expandedColumnWidth = expandedCount > 0
    ? `calc((100% - 384px - ${qualsWidth}px - ${collapsedWidth}px) / ${expandedCount})`
    : 'auto';

  // Driver column + optional Quals column + the 7 day columns - used by every
  // full-width row (group headers, empty state)
  const totalColumns = 1 + (showQualifications ? 1 : 0) + 7;

  // One driver row, shared by the grouped and flat views. showDispatcherName is
  // for the ungrouped views, where the dispatcher isn't implied by a header row.
  const renderDriverRow = (
    driver: DriverPlanningWeekData,
    dispatcher: string,
    showDispatcherName: boolean
  ) => (
    <tr key={`${dispatcher}-${driver.driver}`} className="hover:bg-highlight">
      <td className="px-1 py-0.5 text-gray-700 dark:text-gray-200 border-r border-rule">
        <div className="font-medium text-xs flex items-center gap-1 flex-wrap leading-tight">
          {driver.driver}<MuleBadge isMule={driver.isMule} />
          {driver.driver_name && (
            <span className="text-gray-500 dark:text-white font-normal">{toTitleCase(driver.driver_name)}</span>
          )}
          <DriverPhoneLink textmsgnumber={driver.textmsgnumber} homephone={driver.homephone} />
          {showDispatcherName && (
            <span className="text-[0.625rem] text-gray-400">{dispatcher}</span>
          )}
          {driver.preference && (
            <span className={`text-[0.625rem] px-0.5 rounded ${
              driver.preference.toUpperCase() === 'OTR'
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                : 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
            }`}>
              {driver.preference.toUpperCase()}
            </span>
          )}
          {(() => {
            const isDarkMode = document.documentElement.classList.contains('dark');
            const colorStyles = getParkingColorStyles(driver.parking_color, isDarkMode);
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openParkingModal(driver);
                }}
                className={`text-[0.625rem] px-1 py-0 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                  !colorStyles && (driver.parking_location_name
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                    : 'bg-gray-100 text-muted dark:bg-gray-700')
                }`}
                style={colorStyles || undefined}
                title={driver.parking_location_name || 'Set parking location'}
              >
                {driver.parking_location_name || 'Parking'}
              </button>
            );
          })()}
          {(() => {
            const startLabel = formatStartTimeChip(driver.start_time, driver.start_time_qualifier);
            return startLabel ? (
              <span
                className="text-[0.625rem] px-1 py-0 rounded bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300 whitespace-nowrap"
                title={`Start time: ${startLabel}`}
              >
                {startLabel}
              </span>
            ) : null;
          })()}
        </div>
      </td>
      {showQualifications && (
        <td className="p-1 border-r border-rule align-top w-[100px]">
          <div className="flex flex-wrap gap-0.5">
            {parseQualifications(driver.endorsements).map((endorsement, idx) => (
              <TagBadge
                key={idx}
                label={endorsement}
                color={getQualificationColor(endorsement)}
                className="whitespace-nowrap text-[0.625rem]"
              />
            ))}
          </div>
        </td>
      )}
      {weekDays.map((day, dayIndex) => {
        const dateKey = formatDateKey(day);
        const dayData = driver.delivery_data[dateKey];
        const isToday = dateKey === formatLocalDateKey(new Date());
        const isCollapsed = collapsedDays.has(dayIndex);
        const hasDeliveries = dayData && dayData.delivery_counts.length > 0;
        // hasStatus is only true for OOS and Not Working (statuses that display with special backgrounds)
        const isOosOrNotWorking = dayData?.status === 'Out Of Service' || dayData?.status === 'Not Working' || dayData?.status === 'NOT WORKING';
        const hasStatus = isOosOrNotWorking && !hasDeliveries;
        const statusColorClass = hasStatus ? getStatusColorClass(dayData.status) : '';

        // When status toggle is off, fill cell with status color instead of showing badge
        const badge = getWorkingStatusBadge({
          status: dayData?.status,
          hasDeliveries,
          region: driver.region,
          permitsRequested: dayData?.permits_requested,
          portAppointmentMade: dayData?.port_appointment_made,
          confirmed: dayData?.confirmed,
          workingcategory: dayData?.workingcategory
        });
        // Convert badge color class to background fill with text color (when showStatus is off)
        // Using same colors as Working/Not Working tab - only apply when no deliveries
        const showStatusFill = !showStatus && badge && !hasDeliveries && !hasStatus;
        const actualStatus = dayData?.status;
        const statusFillClass = showStatusFill ? getStatusFillClass(badge.colorClass, actualStatus) : '';

        return (
          <td
            key={dateKey}
            className={`px-1 py-0.5 border-r border-gray-200 dark:border-gray-600 text-xs ${
              showStatusFill ? 'align-middle text-center' : 'align-top'
            } ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px]' : ''} ${isToday ? 'bg-purple-50 dark:bg-purple-900/20' : ''} ${
              !isCollapsed ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''
            } ${statusColorClass} ${statusFillClass} transition-all duration-200`}
            style={isCollapsed ? undefined : { width: expandedColumnWidth }}
            onClick={() => !isCollapsed && handleCellClick(driver.driver, driver.driver_name, dateKey)}
          >
            {isCollapsed ? (
              // Show colored dots for each delivery when collapsed
              hasDeliveries ? (
                <div className="flex flex-col items-center gap-0.5">
                  {dayData.delivery_counts.map((delivery, idx) => {
                    const { color } = getLegBadgeInfo(delivery);
                    const dotColorMap: Record<string, string> = {
                      yellow: 'bg-yellow-400', purple: 'bg-purple-500', blue: 'bg-blue-500',
                      green: 'bg-green-500', orange: 'bg-orange-500', red: 'bg-red-500', gray: 'bg-gray-400'
                    };
                    const dotColor = dotColorMap[color] || 'bg-gray-400';
                    const tooltipText = `${delivery.location_name} x${delivery.count}`;
                    return (
                      <Tooltip key={idx} content={tooltipText}>
                        <span className={`w-2 h-2 rounded-full ${dotColor} cursor-pointer`}></span>
                      </Tooltip>
                    );
                  })}
                </div>
              ) : hasStatus ? (
                <div className="flex justify-center">
                  <Tooltip content={dayData.status || ''}>
                    <span className={`w-2 h-2 rounded-full ${dayData.status?.includes('Out') ? 'bg-red-500' : 'bg-blue-400'}`}></span>
                  </Tooltip>
                </div>
              ) : null
            ) : showStatusFill ? (
              // Status fill mode - text centered via cell's align-middle and text-center
              getStatusFillText(actualStatus, dayData?.workingcategory)
            ) : (
              // Normal expanded view
              <div className="relative min-h-[20px]">
                {/* Working status badge: a wide badge (Done For The Day) is a
                    labeled pill above the deliveries at full opacity; the rest
                    stay the faint corner letter. */}
                {showStatus && badge && badge.wide && (
                  <div className="flex mb-0.5">
                    <Tooltip content={badge.tooltip}>
                      <span className={`text-[0.625rem] font-bold px-1 py-0 rounded whitespace-nowrap cursor-default ${badge.colorClass}`}>
                        {badge.text}
                      </span>
                    </Tooltip>
                  </div>
                )}
                {showStatus && badge && !badge.wide && (
                  <Tooltip content={badge.tooltip}>
                    <span className={`absolute top-0 right-0 text-[0.625rem] font-bold px-0.5 py-0 rounded opacity-30 hover:opacity-100 transition-opacity cursor-default ${badge.colorClass}`}>
                      {badge.text}
                    </span>
                  </Tooltip>
                )}
                {hasDeliveries ? (
                  <div className="pr-4">
                    {renderDeliveryBadges(dayData.delivery_counts)}
                  </div>
                ) : hasStatus ? (
                  <div className="h-full flex items-center justify-center min-h-[20px] text-xs">
                    {dayData.status}
                    {(dayData.comments || dayData.oos_comments || dayData.oos_type) && (
                      <Tooltip content={dayData.comments || dayData.oos_type || dayData.oos_comments || ''}>
                        <ChatBubbleBottomCenterTextIcon className="w-3 h-3 absolute top-0 right-0 text-muted" />
                      </Tooltip>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );

  // A LOCAL / OTR sub-group header. `indented` distinguishes a sub-group inside a
  // dispatcher group from a top-level group in the ungrouped view.
  const renderPrefGroupHeader = (
    dispatcher: string,
    bucket: PreferenceBucket,
    label: string,
    count: number,
    isExpanded: boolean,
    indented: boolean
  ) => (
    <tr
      key={`${dispatcher}|${bucket}-header`}
      className="bg-gray-50 dark:bg-gray-750 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
      onClick={() => togglePrefGroup(dispatcher, bucket)}
    >
      <td colSpan={totalColumns} className={`py-0.5 ${indented ? 'pl-6 pr-2' : 'px-2'}`}>
        <div className="flex items-center gap-1">
          <ChevronRightIcon
            className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          />
          <span className="font-semibold text-xs text-gray-700 dark:text-gray-200">
            {label}
          </span>
          <span className="text-xs text-muted">
            ({count})
          </span>
        </div>
      </td>
    </tr>
  );

  // Flat (ungrouped) driver list, honoring an active column sort
  const flatDrivers = sortedFlatDrivers || filteredData.flatMap(group =>
    group.drivers.map(driver => ({ driver, dispatcher: group.dispatcher }))
  );

  return (
    <>
    <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto mb-16">
      <table className="w-full border-collapse table-fixed">
        {/* Header */}
        <thead className="bg-slate-600 dark:bg-slate-700 text-white sticky top-0 z-30">
          <tr>
            <th className="px-2 py-1 text-center font-semibold border-r border-slate-500 whitespace-nowrap w-96 text-xs">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => onNavigateWeek(-1)}
                    disabled={!canGoBack}
                    className={`p-1 rounded ${canGoBack ? 'hover:bg-slate-500' : 'opacity-40 cursor-not-allowed'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 fill-current rotate-180">
                      <path d="M15.75,9.525,11.164,4.939A1.5,1.5,0,0,0,9.043,7.061l4.586,4.585a.5.5,0,0,1,0,.708L9.043,16.939a1.5,1.5,0,0,0,2.121,2.122l4.586-4.586A3.505,3.505,0,0,0,15.75,9.525Z"/>
                    </svg>
                  </button>
                  <select
                    value={formatDateKey(weekStart)}
                    onChange={(e) => onWeekChange(e.target.value)}
                    className="bg-slate-500 text-white border-none rounded px-2 py-1 text-sm font-semibold cursor-pointer hover:bg-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    {weekOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => onNavigateWeek(1)}
                    disabled={!canGoForward}
                    className={`p-1 rounded ${canGoForward ? 'hover:bg-slate-500' : 'opacity-40 cursor-not-allowed'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M15.75,9.525,11.164,4.939A1.5,1.5,0,0,0,9.043,7.061l4.586,4.585a.5.5,0,0,1,0,.708L9.043,16.939a1.5,1.5,0,0,0,2.121,2.122l4.586-4.586A3.505,3.505,0,0,0,15.75,9.525Z"/>
                    </svg>
                  </button>
                </div>
                <button
                  onClick={() => handleSort('driver')}
                  className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100 hover:bg-slate-500 px-2 py-0.5 rounded"
                  title="Sort by unit number"
                >
                  <span>Unit #</span>
                  {sortColumn === 'driver' ? (
                    sortDirection === 'asc' ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />
                  ) : (
                    <ChevronUpIcon className="h-3 w-3 opacity-30" />
                  )}
                </button>
              </div>
            </th>
            {showQualifications && (
              <th className="px-1 py-1 text-center text-xs font-semibold border-r border-slate-500 whitespace-nowrap w-[100px]">
                Quals
              </th>
            )}
            {weekDays.map((day, index) => {
              const { day: dayName, initial, date } = formatDayHeader(day);
              const isToday = formatDateKey(day) === formatLocalDateKey(new Date());
              const isCollapsed = collapsedDays.has(index);
              return (
                <th
                  key={index}
                  className={`px-1 py-1 text-center text-xs font-semibold whitespace-nowrap ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px]' : ''} ${index < 6 ? 'border-r border-slate-500' : ''} ${isToday ? 'bg-slate-800' : ''} transition-all duration-200`}
                  style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                >
                  {isCollapsed ? (
                    <button
                      onClick={() => toggleDayCollapsed(index)}
                      className="flex items-center justify-center gap-0.5 hover:bg-slate-500 rounded p-1"
                      title="Expand day"
                    >
                      <span className="text-sm font-bold">{initial}</span>
                      <ChevronRightIcon className="h-3 w-3" />
                    </button>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => toggleDayCollapsed(index)}
                          className="p-0.5 hover:bg-slate-500 rounded"
                          title="Collapse day"
                        >
                          <ChevronRightIcon className="h-3 w-3 rotate-180" />
                        </button>
                        <span className="text-xs">{dayName}</span>
                        <span className="text-sm font-bold">{date}</span>
                      </div>
                      <button
                        onClick={() => handleSort(index)}
                        className="flex items-center gap-1 text-[0.625rem] opacity-70 hover:opacity-100 hover:bg-slate-500 px-1 py-0 rounded"
                        title="Sort by number of moves"
                      >
                        <span># Of Moves</span>
                        {sortColumn === index ? (
                          sortDirection === 'asc' ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />
                        ) : (
                          <ChevronDownIcon className="h-3 w-3 opacity-30" />
                        )}
                      </button>
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
          {/* Filter Row */}
          <tr className="bg-slate-500 relative z-30">
            <th className="p-1 text-center border-r border-slate-400">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter driver"
                className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
            {showQualifications && (
              <th className="p-1 border-r border-slate-400 w-[100px]"></th>
            )}
            {weekDays.map((day, index) => {
              const isToday = formatDateKey(day) === formatLocalDateKey(new Date());
              const isCollapsed = collapsedDays.has(index);
              const counts = getStatusCountsForDay(index);
              const statusOptionsWithCounts = statusFilterOptions.map(opt => ({
                ...opt,
                label: `${opt.label} (${counts[opt.value] || 0})`
              }));
              return (
                <th key={index} className={`p-1 ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px]' : ''} ${index < 6 ? 'border-r border-slate-400' : ''} ${isToday ? 'bg-slate-800' : ''} transition-all duration-200`} style={isCollapsed ? undefined : { width: expandedColumnWidth }}>
                  {!isCollapsed && (
                    <MultiSelect
                      options={statusOptionsWithCounts}
                      value={columnFilters[index] || []}
                      onChange={(value: string[]) => {
                        setColumnFilters(prev => ({
                          ...prev,
                          [index]: value
                        }));
                      }}
                      placeholder="All"
                      showClearOption={true}
                      clearOptionLabel="All"
                      enableSearch={false}
                      size="sm"
                      className="w-full"
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        {/* Body */}
        <tbody className="bg-panel divide-y divide-gray-200 dark:divide-gray-700">
          {filteredData.length === 0 ? (
            <tr>
              <td colSpan={totalColumns} className="p-4 text-center text-xs text-muted">
                {loadFailed
                  ? 'Couldn\'t load planning data — the request failed. Refresh to retry.'
                  : 'No data found for the selected filters'}
              </td>
            </tr>
          ) : groupByDispatcher ? (
            // Grouped view
            filteredData.map((group) => {
              const isExpanded = expandedDispatchers.has(group.dispatcher);
              return (
                <React.Fragment key={group.dispatcher}>
                  {/* Dispatcher header row */}
                  <tr
                    className="bg-gray-100 dark:bg-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                    onClick={() => toggleDispatcher(group.dispatcher)}
                  >
                    <td colSpan={totalColumns} className="px-2 py-0.5">
                      <div className="flex items-center gap-1">
                        <ChevronRightIcon
                          className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                        <span className="font-semibold text-xs text-heading">
                          {group.dispatcher}
                        </span>
                        <span className="text-xs text-muted">
                          ({group.drivers.length})
                        </span>
                      </div>
                    </td>
                  </tr>
                  {/* Driver rows, optionally split into LOCAL / OTR sub-groups */}
                  {isExpanded && (groupByPreference
                    ? groupByPreferenceBucket(group.drivers).map(prefGroup => {
                        const isPrefExpanded = !collapsedPrefGroups.has(
                          prefGroupKey(group.dispatcher, prefGroup.bucket)
                        );
                        return (
                          <React.Fragment key={prefGroup.bucket}>
                            {renderPrefGroupHeader(
                              group.dispatcher,
                              prefGroup.bucket,
                              prefGroup.label,
                              prefGroup.drivers.length,
                              isPrefExpanded,
                              true
                            )}
                            {isPrefExpanded && prefGroup.drivers.map(driver =>
                              renderDriverRow(driver, group.dispatcher, false)
                            )}
                          </React.Fragment>
                        );
                      })
                    : group.drivers.map(driver => renderDriverRow(driver, group.dispatcher, false))
                  )}
                </React.Fragment>
              );
            })
          ) : groupByPreference ? (
            // Ungrouped, but split into top-level LOCAL / OTR groups
            groupByPreferenceBucket(flatDrivers, entry => entry.driver.preference).map(prefGroup => {
              const isPrefExpanded = !collapsedPrefGroups.has(prefGroupKey('', prefGroup.bucket));
              return (
                <React.Fragment key={prefGroup.bucket}>
                  {renderPrefGroupHeader(
                    '',
                    prefGroup.bucket,
                    prefGroup.label,
                    prefGroup.drivers.length,
                    isPrefExpanded,
                    false
                  )}
                  {isPrefExpanded && prefGroup.drivers.map(({ driver, dispatcher }) =>
                    renderDriverRow(driver, dispatcher, true)
                  )}
                </React.Fragment>
              );
            })
          ) : (
            // Flat view (not grouped) - use sortedFlatDrivers when sorting is active
            flatDrivers.map(({ driver, dispatcher }) => renderDriverRow(driver, dispatcher, true))
          )}
        </tbody>

        {/* Sticky Footer with Statistics */}
        <tfoot className="bg-slate-600 dark:bg-slate-700 text-white sticky bottom-0 z-10">
          {/* Total Deliveries Row */}
          <tr>
            <td className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500">
              Total Deliveries
            </td>
            {showQualifications && <td className="border-r border-slate-500 w-[100px]"></td>}
            {dailyStats.map((stat, index) => {
              const isToday = formatDateKey(weekDays[index]) === formatLocalDateKey(new Date());
              const isCollapsed = collapsedDays.has(index);
              return (
                <td
                  key={index}
                  className={`px-1 py-0.5 text-center text-xs font-semibold ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px] text-xs' : ''} ${index < 6 ? 'border-r border-slate-500' : ''} ${isToday ? 'bg-slate-800' : ''} transition-all duration-200`}
                  style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                >
                  {stat.totalDeliveries}
                </td>
              );
            })}
          </tr>
          {/* Accepted Deliveries Row */}
          <tr>
            <td className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500">
              Accepted Deliveries
            </td>
            {showQualifications && <td className="border-r border-slate-500 w-[100px]"></td>}
            {dailyStats.map((stat, index) => {
              const isToday = formatDateKey(weekDays[index]) === formatLocalDateKey(new Date());
              const isCollapsed = collapsedDays.has(index);
              return (
                <td
                  key={index}
                  className={`px-1 py-0.5 text-center text-xs font-semibold ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px] text-xs' : ''} ${index < 6 ? 'border-r border-slate-500' : ''} ${isToday ? 'bg-slate-800' : ''} transition-all duration-200`}
                  style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                >
                  {stat.acceptedDeliveries}
                </td>
              );
            })}
          </tr>
          {/* Percentage Accepted Row */}
          <tr>
            <td className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500">
              Percentage Accepted
            </td>
            {showQualifications && <td className="border-r border-slate-500 w-[100px]"></td>}
            {dailyStats.map((stat, index) => {
              const isToday = formatDateKey(weekDays[index]) === formatLocalDateKey(new Date());
              const isCollapsed = collapsedDays.has(index);
              // Color: green if >= 85%, red if < 85%, or slate-800 for today with no data
              const bgColor = stat.totalDeliveries === 0
                ? (isToday ? 'bg-slate-800' : '')
                : (stat.percentage >= 85 ? 'bg-green-500' : 'bg-red-500');
              return (
                <td
                  key={index}
                  className={`px-1 py-0.5 text-center text-xs font-semibold ${isCollapsed ? 'w-12 min-w-[48px] max-w-[48px] text-xs' : ''} ${index < 6 ? 'border-r border-slate-500' : ''} ${bgColor} transition-all duration-200`}
                  style={isCollapsed ? undefined : { width: expandedColumnWidth }}
                >
                  {isCollapsed ? (stat.totalDeliveries > 0 ? `${Math.round(stat.percentage)}%` : '-') : (stat.totalDeliveries > 0 ? `${stat.percentage.toFixed(2)}%` : '-')}
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>

    {/* Parking Location Modal */}
    <ParkingLocationModal
      isOpen={parkingModalOpen}
      onClose={() => setParkingModalOpen(false)}
      driver={parkingModalDriver}
      driverName={parkingModalDriverName}
      currentParkingloc={parkingModalCurrentLoc}
      currentParkingName={parkingModalCurrentName}
      currentParkingColor={parkingModalCurrentColor}
      onSave={handleParkingLocationSave}
    />
    </>
  );
};

export default DriverPlanningWeek;
